<template>
    <table class="Datepicker-dropdown-calendar">
        <thead>
            <tr>
                <th tabindex="0" :class="prevMonthClass" @click="prevMonthClick">
                    <div>
                        <i class="isax isax-arrow-left"/>
                    </div>
                </th>

                <th v-if="showDropdowns" class="month" :colspan="showWeekNumbers ? 6 : 5">
                    <div class="dateSelect">
                        <select class="Select sm" v-model="month">
                            <option v-for="( m ) in months" :key="m.value" :value="m.value + 1">{{ m.label }}</option>
                        </select>

                        <input ref="yearSelect" class="sm" type="number" v-model="year" @blur="checkYear" />
                    </div>
                </th>
                <th v-else :colspan="showWeekNumbers ? 6 : 5" class="month">
                    <div>{{ monthName }} {{ year }}</div>
                </th>

                <th tabindex="0" :class="nextMonthClass" @click="nextMonthClick">
                    <div>
                        <i class="isax isax-arrow-right-1" />
                    </div>
                </th>
            </tr>
        </thead>


        <tbody>
            <tr>
                <th v-if="showWeekNumbers" class="week">
                    <div>{{ locale.weekLabel }}</div>
                </th>

                <th v-for="weekDay in locale.daysOfWeek" :key="weekDay">
                    <div>{{ weekDay }}</div>
                </th>
            </tr>

            <tr class="month-dates" v-for="(dateRow, index) in calendar" :key="index">
                <td v-if="showWeekNumbers && (index%7 || index===0)" class="week">
                    <div>{{ $dateUtil.weekNumber(dateRow[0]) }}</div>
                </td>

                <td v-for="( date, idx ) in dateRow" :class="dayClass( date )" @click="dateClick( date )" @mouseover="$emit( 'hoverDate', date )" :key="idx">
                    <slot name="date-slot" :date="date">
                        <div>{{ date.getDate() }}</div>
                    </slot>
                </td>
            </tr>
        </tbody>
    </table>
</template>

<script>
import date     from './utils/date.js';

import props    from './utils/Calendar/props.js';
import watches  from './utils/Calendar/watches.js';
import methods  from './utils/Calendar/methods.js';
import computed from './utils/Calendar/computed.js';

export default
{
    name   : 'Calendar',
    mixins : [ date, props, computed, methods, watches ],

    data()
    {
        let currentMonthDate = this.monthDate || this.start || new Date();

        return { currentMonthDate, year_text: currentMonthDate.getFullYear() }
    }
}
</script>

<style lang="scss">
body
{
    &[data-theme="light"]
    {
        .Datepicker-dropdown-calendar
        {
            --datepicker-dropdown-calendar-range-border-color: var(--layout-border-color);
            --datepicker-dropdown-calendar-range-thead-color: var(--dark-surfaces-gunmetal);
            --datepicker-dropdown-calendar-range-thead-icon-color: #696A80;

            --datepicker-dropdown-calendar-range-thead-month-color: var(--light-text-color-secondary);

            --datepicker-dropdown-calendar-range-tbody-week-color: var(--secondary-text-color);

            --datepicker-dropdown-calendar-range-tbody-date-color: var(--light-text-color-primary);

            --datepicker-dropdown-calendar-range-tbody-date-hover-color: var(--light-text-color-primary);
            --datepicker-dropdown-calendar-range-tbody-date-hover-background-color: var(--primary-colors-blue-10);

            --datepicker-dropdown-calendar-range-tbody-date-active-color: var(--light-surfaces-white);
            --datepicker-dropdown-calendar-range-tbody-date-active-border-color: var(--primary-colors-blue-60);
            --datepicker-dropdown-calendar-range-tbody-date-active-background-color: var(--primary-colors-blue-60);

            --datepicker-dropdown-calendar-range-tbody-date-range-color: var(--primary-colors-blue-10);

            --datepicker-dropdown-calendar-range-tbody-today-color: var(--primary-colors-blue-60);
            --datepicker-dropdown-calendar-range-tbody-today-border-color: var(--primary-colors-blue-20);
            --datepicker-dropdown-calendar-range-tbody-today-background-color: var(--primary-colors-blue-10);

        }
    }

    &[data-theme="dark"]
    {
        .Datepicker-dropdown-calendar
        {
            --datepicker-dropdown-calendar-range-border-color: var(--layout-border-color);
            --datepicker-dropdown-calendar-range-thead-color: var(--dark-text-color-primary);
            --datepicker-dropdown-calendar-range-thead-icon-color: #B0B3BF;

            --datepicker-dropdown-calendar-range-thead-month-color: var(--dark-text-color-secondary);

            --datepicker-dropdown-calendar-range-tbody-week-color: var(--secondary-text-color);

            --datepicker-dropdown-calendar-range-tbody-date-color: var(--dark-text-color-primary);

            --datepicker-dropdown-calendar-range-tbody-date-hover-color: var(--light-text-color-primary);
            --datepicker-dropdown-calendar-range-tbody-date-hover-background-color: var(--primary-colors-blue-30);

            --datepicker-dropdown-calendar-range-tbody-date-active-color: var(--dark-surfaces-dark-jungle);
            --datepicker-dropdown-calendar-range-tbody-date-active-border-color: var(--primary-colors-blue-30);
            --datepicker-dropdown-calendar-range-tbody-date-active-background-color: var(--primary-colors-blue-30);

            --datepicker-dropdown-calendar-range-tbody-date-range-color: var(--layout-primary-background-color);

            --datepicker-dropdown-calendar-range-tbody-today-color: var(--dark-surfaces-dark-jungle);
            --datepicker-dropdown-calendar-range-tbody-today-border-color: var(--primary-colors-blue-30);
            --datepicker-dropdown-calendar-range-tbody-today-background-color: var(--primary-colors-blue-50);
        }
    }
}

.Datepicker-dropdown-calendar
{
    display: table;
    border-collapse: collapse;

    width: 100%;

    margin: 0;
    border-spacing: 0;

    thead
    {
        tr
        {
            border-bottom: 1px solid var(--datepicker-dropdown-calendar-range-border-color);

            th
            {
                @include font-body-sm-semibold;

                margin: 0;
                padding: 0;

                color: var(--datepicker-dropdown-calendar-range-thead-color);

                border: 0;

                &.prev,
                &.next
                {
                    &.disabled
                    {
                        cursor: not-allowed;

                        > div
                        {
                            pointer-events: none;
                            opacity: 0.4;
                        }
                    }

                    > div
                    {
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        padding-bottom: px(16);

                        > i
                        {
                            width: px(24);
                            height: px(24);
                            font-size: px(24);

                            color: var(--datepicker-dropdown-calendar-range-thead-icon-color);
                        }
                    }
                }

                &.month
                {
                    > div:not(.dateSelect)
                    {
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        padding-bottom: px(16);
                    }

                    > div.dateSelect
                    {
                        display: flex;
                        align-items: center;
                        justify-content: center;

                        gap: px(12);

                        width: 100%;
                        height: 100%;

                        padding-bottom: px(16);

                        select, input
                        {
                            text-align: center;

                            cursor: pointer;
                        }

                        .input
                        {
                            appearance: menulist;
                        }
                    }
                }
            }
        }
    }

    tbody
    {
        tr
        {
            th, td
            {
                border: 0;
                padding: 0;
                margin: 0;

                > div
                {
                    display: flex;
                    align-items: center;
                    justify-content: center;

                    width: px(48);
                    height: px(48);
                    line-height: px(48);
                }
            }

            th
            {
                @include font-caption-md-semibold;

                color: var(--datepicker-dropdown-calendar-range-thead-month-color);

                border-bottom: 1px solid var(--datepicker-dropdown-calendar-range-border-color);
            }

            &.month-dates
            {
                td
                {
                    @include font-body-sm-semibold;

                    color: var(--datepicker-dropdown-calendar-range-tbody-date-color);

                    cursor: pointer;

                    &.week
                    {
                        @include font-caption-md-semibold;

                        color: var(--datepicker-dropdown-calendar-range-tbody-week-color);
                    }

                    &:not(.week)
                    {
                        &:not(.in-range):not(.active):not(.disabled):not(.start-date):not(.end-date):hover > div
                        {
                            color: var(--datepicker-dropdown-calendar-range-tbody-date-hover-color);
                            background-color: var(--datepicker-dropdown-calendar-range-tbody-date-hover-background-color);
                            border-color: var(--datepicker-dropdown-calendar-range-tbody-date-active-border-color);

                            border-radius: 50%;
                        }

                        &.off > div
                        {
                            opacity: 0;
                            visibility: hidden;
                            user-select: none;
                            pointer-events: none;
                        }

                        &.weekend:not(.active):not(.disabled):not(.start-date):not(.end-date) > div
                        {
                            opacity: 0.5;
                        }

                        &.today:not(.in-range):not(.active):not(.disabled):not(.start-date):not(.end-date) > div
                        {
                            color: var(--datepicker-dropdown-calendar-range-tbody-today-color);
                            border: 1.5px solid var(--datepicker-dropdown-calendar-range-tbody-today-border-color);
                            background-color: var(--datepicker-dropdown-calendar-range-tbody-today-background-color);

                            border-radius: 50%;
                        }

                        &.active > div
                        {
                            color: var(--datepicker-dropdown-calendar-range-tbody-date-active-color);
                            background-color: var(--datepicker-dropdown-calendar-range-tbody-date-active-background-color);
                            border-color: var(--datepicker-dropdown-calendar-range-tbody-date-active-border-color);
                            border-radius: 50%;
                        }

                        &.in-range > div
                        {
                            background-color: var(--datepicker-dropdown-calendar-range-tbody-date-range-color);
                        }

                        &.start-date > div,
                        &.end-date > div
                        {
                            position: relative;

                            color: var(--datepicker-dropdown-calendar-range-tbody-date-active-color);
                            background-color: var(--datepicker-dropdown-calendar-range-tbody-date-active-background-color);
                            border-color: var(--datepicker-dropdown-calendar-range-tbody-date-active-border-color);
                            border-radius: 50%;

                            /*
                            &::after
                            {
                                content: '';
                                position: absolute;
                                top: 0;
                                width: 50%;
                                height: 100%;
                                background-color: var(--datepicker-dropdown-calendar-range-tbody-date-range-color);

                                z-index: -1;
                            }
                            */
                        }

                        /*
                        &.start-date > div::after
                        {
                            right: 0;
                        }

                        &.end-date > div::after
                        {
                            left: 0;
                        }
                        */

                        &.disabled
                        {
                            cursor: not-allowed;

                            > div
                            {
                                pointer-events: none;
                                background-color: silver;
                                border-radius: 0;
                                opacity: 0.6;
                            }
                        }

                        &.disabled-min-last > div,
                        &.disabled-max-last > div
                        {
                            border-top-right-radius: 50%;
                            border-bottom-right-radius: 50%;
                        }

                        &.disabled-min-first > div,
                        &.disabled-max-first > div
                        {
                            border-top-left-radius: 50%;
                            border-bottom-left-radius: 50%;
                        }
                    }
                }
            }
        }
    }
}
</style>